import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { styles as S } from '../styles';
import { STUNDEN, TAGE, TYP_LABEL, parseTime, formatTime, datumImWochentag } from '../utils';
import { IconPin, IconRepeat, IconWarn } from '../icons';
const ROW = 56; // Pixel pro Stunde
const SNAP_MIN = 15; // Raster für Zeiten (Minuten)
function zuHHMM(h) {
    let m = Math.round(h * 60);
    m = Math.max(0, Math.min(24 * 60, m));
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
export function RasterView({ platz, belegungen, sperren = [], teamById, konflikte, onSelect, montag, editierbar = false, onMove, }) {
    const offVon = platz.oeffnung_von ? parseTime(platz.oeffnung_von) : null;
    const offBis = platz.oeffnung_bis ? parseTime(platz.oeffnung_bis) : null;
    const geschlossen = (std) => (offVon !== null && std + 1 <= offVon) || (offBis !== null && std >= offBis);
    const dragRef = useRef(null);
    const colWidthRef = useRef(140);
    const tagHeadRef = useRef(null);
    const [, force] = useState(0);
    function start(e, b, modus) {
        if (!editierbar || b._status === 'entfaellt') {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (tagHeadRef.current) {
            colWidthRef.current = tagHeadRef.current.offsetWidth || colWidthRef.current;
        }
        dragRef.current = { id: b.id, b, modus, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0, moved: false };
        force((n) => n + 1);
    }
    function commit(d) {
        if (!onMove) {
            return;
        }
        const cw = colWidthRef.current || 140;
        const von = parseTime(d.b.start_zeit);
        const bis = parseTime(d.b.end_zeit);
        const dauer = bis - von;
        const hShift = (Math.round((d.dy / ROW * 60) / SNAP_MIN) * SNAP_MIN) / 60;
        const minStart = STUNDEN[0];
        const maxEnde = STUNDEN[STUNDEN.length - 1] + 1;
        if (d.modus === 'resize') {
            const neuBis = Math.min(maxEnde, Math.max(von + 0.25, bis + hShift));
            if (Math.abs(neuBis - bis) < 0.001) {
                return;
            }
            onMove(d.b, { wochentag: d.b.wochentag, start_zeit: zuHHMM(von), end_zeit: zuHHMM(neuBis) });
        }
        else {
            const tagShift = Math.round(d.dx / cw);
            const neuTag = Math.min(7, Math.max(1, (d.b.wochentag || 1) + tagShift));
            const neuVon = Math.min(maxEnde - dauer, Math.max(minStart, von + hShift));
            if (neuTag === d.b.wochentag && Math.abs(neuVon - von) < 0.001) {
                return;
            }
            onMove(d.b, { wochentag: neuTag, start_zeit: zuHHMM(neuVon), end_zeit: zuHHMM(neuVon + dauer) });
        }
    }
    useEffect(() => {
        function bewegung(e) {
            const d = dragRef.current;
            if (!d) {
                return;
            }
            d.dx = e.clientX - d.startX;
            d.dy = e.clientY - d.startY;
            if (Math.abs(d.dx) > 4 || Math.abs(d.dy) > 4) {
                d.moved = true;
            }
            force((n) => n + 1);
        }
        function loslassen() {
            const d = dragRef.current;
            if (!d) {
                return;
            }
            dragRef.current = null;
            if (!d.moved) {
                onSelect(d.b);
            }
            else {
                commit(d);
            }
            force((n) => n + 1);
        }
        window.addEventListener('pointermove', bewegung);
        window.addEventListener('pointerup', loslassen);
        return () => {
            window.removeEventListener('pointermove', bewegung);
            window.removeEventListener('pointerup', loslassen);
        };
    });
    const aktiv = dragRef.current;
    return (_jsx("div", { style: S.rasterWrap, children: _jsxs("div", { style: S.raster, children: [_jsx("div", { style: S.rasterCorner, children: _jsx(IconPin, { size: 14, style: { color: platz.farbe } }) }), TAGE.map((t, i) => {
                    const d = datumImWochentag(montag, i + 1);
                    return (_jsxs("div", { ref: i === 0 ? tagHeadRef : undefined, style: S.tagHead, children: [_jsx("span", { style: S.tagKurz, children: t }), _jsxs("span", { style: S.tagDatum, children: [d.getDate(), ".", d.getMonth() + 1, "."] })] }, t));
                }), STUNDEN.map((std) => (_jsxs(React.Fragment, { children: [_jsxs("div", { style: S.stundeLabel, children: [String(std).padStart(2, '0'), ":00"] }), TAGE.map((_, tagIdx) => {
                            const tag = tagIdx + 1;
                            const eintraege = belegungen.filter((b) => {
                                const von = parseTime(b.start_zeit);
                                return (b.wochentag === tag &&
                                    (Math.floor(von) === std || (std === STUNDEN[0] && von < std)));
                            });
                            return (_jsxs("div", { style: geschlossen(std) ? { ...S.slot, background: '#fafafa' } : S.slot, children: [sperren
                                        .filter((s) => {
                                        if (s.wochentag !== tag)
                                            return false;
                                        if (s.ganztags)
                                            return true;
                                        const sv = parseTime(s.start_zeit || '00:00');
                                        const sb = parseTime(s.end_zeit || '24:00');
                                        return sv < std + 1 && sb > std;
                                    })
                                        .map((s) => {
                                        const startStd = s.ganztags
                                            ? STUNDEN[0]
                                            : Math.floor(parseTime(s.start_zeit || '00:00'));
                                        const istStart = startStd === std || (std === STUNDEN[0] && startStd < std);
                                        return (_jsx("div", { style: S.rasterSperre, title: s.grund || 'Gesperrt', children: istStart && (_jsxs("span", { style: S.rasterSperreText, children: ["\uD83D\uDD12 ", s.grund || 'Gesperrt'] })) }, `sp-${s.id}`));
                                    }), eintraege.map((b) => {
                                        const von = parseTime(b.start_zeit);
                                        const bis = parseTime(b.end_zeit);
                                        const top = Math.max(0, von - std) * ROW;
                                        const hoehe = (bis - von) * ROW;
                                        const team = teamById(b.team_id);
                                        const konflikt = konflikte.has(b.id);
                                        const entfaellt = b._status === 'entfaellt';
                                        const geaendert = b._status === 'geaendert';
                                        const angefragt = b.status === 'angefragt';
                                        const istDrag = aktiv?.id === b.id;
                                        let dragStyle = {};
                                        if (istDrag && aktiv) {
                                            dragStyle = aktiv.modus === 'move'
                                                ? { transform: `translate(${aktiv.dx}px, ${aktiv.dy}px)`, zIndex: 50, boxShadow: '0 6px 16px rgba(0,0,0,0.18)', cursor: 'grabbing', opacity: 0.92 }
                                                : { height: Math.max(18, (hoehe - 4) + aktiv.dy), zIndex: 50, cursor: 'ns-resize' };
                                        }
                                        return (_jsxs("div", { role: "button", tabIndex: 0, onClick: editierbar ? undefined : () => onSelect(b), onPointerDown: editierbar ? (e) => start(e, b, 'move') : undefined, onKeyDown: (e) => e.key === 'Enter' && onSelect(b), style: {
                                                ...S.eintrag,
                                                top,
                                                height: hoehe - 4,
                                                background: entfaellt ? '#f4f5f3' : angefragt ? '#fef9e7' : konflikt ? '#fef2f2' : `${platz.farbe}14`,
                                                borderLeft: `3px solid ${entfaellt ? '#bcc2b8' : konflikt ? '#dc2626' : angefragt ? '#d4a017' : platz.farbe}`,
                                                border: angefragt ? '1px dashed #d4a017' : undefined,
                                                borderLeftWidth: 3,
                                                opacity: entfaellt ? 0.7 : 1,
                                                cursor: editierbar && !entfaellt ? 'grab' : 'pointer',
                                                touchAction: editierbar ? 'none' : undefined,
                                                ...dragStyle,
                                            }, children: [_jsxs("div", { style: S.eintragKopf, children: [_jsx("strong", { style: {
                                                                ...S.eintragTeam,
                                                                textDecoration: entfaellt ? 'line-through' : 'none',
                                                            }, children: team?.name ?? b.titel ?? '—' }), b.wiederkehrend && !entfaellt && _jsx(IconRepeat, { size: 11, style: { opacity: 0.5 } }), geaendert && _jsx("span", { style: S.geaendertDot, title: "Ge\u00E4nderter Termin" }), konflikt && !entfaellt && _jsx(IconWarn, { size: 11, style: { color: '#dc2626' } })] }), _jsx("div", { style: S.eintragZeit, children: entfaellt ? 'Entfällt' : `${formatTime(von)}–${formatTime(bis)}` }), _jsx("div", { style: S.eintragTyp, children: TYP_LABEL[b.typ] }), editierbar && !entfaellt && (_jsx("div", { onPointerDown: (e) => start(e, b, 'resize'), title: "Dauer \u00E4ndern", style: S.resizeGriff }))] }, b.id));
                                    })] }, tag));
                        })] }, std)))] }) }));
}
